import { Component, EventEmitter, Input, Output } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { MonitoringService } from 'src/app/services/monitoring/monitoring.service';
import { DevicePermissionsService } from 'src/app/services/utils/device-permissions.service';
import { GeolocationService } from 'src/app/services/utils/geolocation.service';
import { MediaDevicesService } from 'src/app/services/utils/media-devices.service';

type PersmissionsStatus = 'initial' | 'loading' | 'completed';
type Permission = {
	name: string;
	type: string;
	isEnabled: boolean;
};

@Component({
	selector: 'cmv-device-permission-modal',
	templateUrl: './device-permission-modal.component.html',
	styleUrls: ['./device-permission-modal.component.scss'],
})
export class DevicePermissionModalComponent {
	permissionStatus: PersmissionsStatus = 'initial';
	continueButtonTexts: Record<PersmissionsStatus, string> = {
		initial: 'Habilitar permisos',
		loading: 'Habilitando permisos ...',
		completed: 'Continuar',
	};
	permissions: Array<Permission> = [
		{
			type: 'geolocation',
			name: 'Ubicación',
			isEnabled: false,
		},
		{
			type: 'camera',
			name: 'Cámara',
			isEnabled: false,
		},
		{
			type: 'microphone',
			name: 'Micrófono',
			isEnabled: false,
		},
	];
	@Input()
	isModalOpen: boolean;
	isNoticeModalVisible: boolean;
	@Output() closeEvent: EventEmitter<void> = new EventEmitter();

	constructor(
		private devicePermissionsService: DevicePermissionsService,
		private geolocationService: GeolocationService,
		private mediaDevicesService: MediaDevicesService,
		private monitoringService: MonitoringService
	) {}

	get continueButtonText(): string {
		return this.continueButtonTexts[this.permissionStatus];
	}

	get isContinueButtonEnabled(): boolean {
		return this.permissionStatus !== 'loading';
	}

	get isGettingPermissions(): boolean {
		return this.permissionStatus === 'loading';
	}

	get deniedPermissions(): string {
		return this.permissions
			.filter(permission => !permission.isEnabled)
			.map(permission => permission.name)
			.join(', ');
	}

	permissionTrackBy(_, permission: Permission) {
		return permission.name;
	}

	onContinue(): void {
		if (this.permissionStatus === 'initial') {
			this.startAskPermissions();
		}

		if (this.permissionStatus === 'completed') {
			this.notifyAndContinue();
		}
	}
	notifyAndContinue() {
		const isAnyDevicePermissionNotEnabled = this.permissions.some(
			permission => !permission.isEnabled
		);
		if (isAnyDevicePermissionNotEnabled) {
			this.monitoringService.captureError(
				Error('No se habilitaron todos los permisos'),
				{ permissions: this.permissions }
			);
			this.isNoticeModalVisible = true;
			return;
		}
		this.closeEvent.emit();
	}

	onCloseNotice(): void {
		this.closeEvent.emit();
	}

	startAskPermissions(): void {
		this.permissionStatus = 'loading';
		this.getAndHandleDevicePermissions();
	}

	private getAndHandleDevicePermissions() {
		forkJoin([
			this.devicePermissionsService.getPermission('geolocation'),
			this.devicePermissionsService.getPermission('camera'),
			this.devicePermissionsService.getPermission('microphone'),
		]).subscribe({
			next: ([geolocationStatus, cameraStatus, microphoneStatus]) => {
				this.handleAllDeviceSates(
					geolocationStatus,
					cameraStatus,
					microphoneStatus
				);
			},
			error: (error: Error) => {
				this.permissionStatus = 'completed';
				this.monitoringService.captureError(
					Error(
						'No se habilitaron todos los permisos, error en manejo de permisos'
					),
					{ permissions: this.permissions, error }
				);
			},
		});
	}

	private handleAllDeviceSates(
		geolocationStatus: string,
		cameraStatus: string,
		microphoneStatus: string
	) {
		forkJoin([
			this.handleGeolocationStatus(geolocationStatus),
			this.handleCameraAndMicrophone(cameraStatus, microphoneStatus),
		]).subscribe({
			next: ([
				geolocationIsEnabled,
				[cameraIsEnabled, microphoneIsEnabled],
			]) => {
				this.updateDeviceStatus(
					geolocationIsEnabled,
					cameraIsEnabled,
					microphoneIsEnabled
				);
				this.permissionStatus = 'completed';
			},
			error: () => {
				this.monitoringService.captureError(
					Error('No se habilitaron todos los permisos'),
					{ permissions: this.permissions }
				);
				this.permissionStatus = 'completed';
			},
		});
	}

	private updateDeviceStatus(
		geolocationIsEnabled: boolean,
		cameraIsEnabled: boolean,
		microphoneIsEnabled: boolean
	) {
		this.permissions = this.permissions.map(permission => {
			switch (permission.type) {
				case 'geolocation':
					return { ...permission, isEnabled: geolocationIsEnabled };
				case 'camera':
					return { ...permission, isEnabled: cameraIsEnabled };
				case 'microphone':
					return { ...permission, isEnabled: microphoneIsEnabled };
				default:
					return permission;
			}
		});
	}

	handleGeolocationStatus(cameraStatus: string): Observable<boolean> {
		return new Observable(observable => {
			if (cameraStatus === 'granted' || cameraStatus === 'denied') {
				observable.next(cameraStatus === 'granted');
				observable.complete();
				return;
			}
			this.geolocationService.getCoords().subscribe({
				next: () => {
					observable.next(true);
					observable.complete();
				},
				error: () => {
					observable.next(false);
					observable.complete();
				},
			});
		});
	}
	handleCameraAndMicrophone(
		cameraStatus: string,
		microphoneStatus: string
	): Observable<[boolean, boolean]> {
		return new Observable(observable => {
			const cameraPermissionRequiresPromt = cameraStatus === 'prompt';
			const microphonePermissionRequiresPromt = microphoneStatus === 'prompt';
			if (
				!cameraPermissionRequiresPromt &&
				!microphonePermissionRequiresPromt
			) {
				observable.next([
					cameraStatus === 'granted',
					microphoneStatus === 'granted',
				]);
				observable.complete();
				return;
			}
			this.mediaDevicesService
				.getUserMedia({ video: true, audio: true })
				.subscribe({
					next: stream => {
						stream.getTracks().forEach(track => track.stop());
						observable.next([true, true]);
						observable.complete();
					},
					error: () => {
						observable.next([false, false]);
						observable.complete();
					},
				});
		});
	}
}
